import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as ReactDOMClient from 'react-dom/client';
import { WidgetsProvider } from '@sitecore-search/react';
import SearchPageHeader from '../search-page-header/s-search-page-header';
import SearchResult from './components/search-result/search-result';
import { SearchResultProvider, useSearchContext } from './components/search-provider/search-context';
import PopularSearchCarousel from '../popular-search-carousel/s-popular-search-carousel';

const SitecoreSearchBlock = () => {
    const { searchResultWidgetId, popularSearchWidgetId, previewSearchWidgetId, currentKeyword, setCurrentKeyword, searchItemsPerPage, totalSearchResults, currentPage, activeFilter } = useSearchContext();
    const pageContentRef = useRef(null);

    // Scroll to search results when keyword, filters, or page changes
    useEffect(() => {
        if (pageContentRef.current && currentKeyword) {
            pageContentRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [currentKeyword, currentPage, activeFilter]);

    const handleSearch = searchKeyword => {
        setCurrentKeyword(searchKeyword);
    };

    const resetKeyword = () => {
        setCurrentKeyword('');
    };

    const getResultsRange = () => {
        const start = (currentPage - 1) * searchItemsPerPage + 1;
        const end = Math.min(currentPage * searchItemsPerPage, totalSearchResults);
        return { start, end };
    };

    const { start, end } = getResultsRange();

    const sectionTitle = currentKeyword ? `Search results for "${currentKeyword}"` : 'Enter your search term';
    let sectionSubtitle = '';
    if (currentKeyword !== '') {
        if (totalSearchResults > 0) {
            sectionSubtitle = `${start} - ${end} of ${totalSearchResults} search results for ${currentKeyword}`;
        } else {
            sectionSubtitle = `No results found for "${currentKeyword}"`;
        }
    }

    return (
        <div>
            <SearchPageHeader
                sectionTitle={sectionTitle}
                sectionSubtitle={sectionSubtitle}
                onSubmit={handleSearch}
                initialSearchValue={currentKeyword}
                rfkId={previewSearchWidgetId}
                onClearSearch={resetKeyword}
            />

            <PopularSearchCarousel
                rfkId={popularSearchWidgetId}
            />

            <div ref={pageContentRef} className="search-page-content">
                <div className="container">
                    <SearchResult
                        rfkId={searchResultWidgetId}
                    />
                </div>
            </div>
        </div>
    );
};

const SitecoreSearchBlockInit = ({el, props = {}}) => {
    const {
        env = 'apse2',
        customerKey = '186718190-16785702162',
        apiKey = '01-3dab73b8-a842cb674e5998facb9344effba40e34541a4e04',
        searchResultWidgetId = 'rfkid_7',
        popularSearchWidgetId = 'rfkid_8',
        previewSearchWidgetId = 'rfkid_6',
        popularSearchSorting = 'top_views',
        popularSearchMaxItems = 8,
        searchItemsPerPage = 10,
        dataSourceId = '1190926'
    } = props;

    const root = ReactDOMClient.createRoot(el);

    root.render(
        <WidgetsProvider
            env={env}
            customerKey={customerKey}
            apiKey={apiKey}
            publicSuffix={true}
        >
            <SearchResultProvider
                dataSource={dataSourceId}
                searchResultWidgetId={searchResultWidgetId}
                popularSearchWidgetId={popularSearchWidgetId}
                previewSearchWidgetId={previewSearchWidgetId}
                popularSearchSorting={popularSearchSorting}
                popularSearchMaxItems={popularSearchMaxItems}
                searchItemsPerPage={searchItemsPerPage}
            >
                {/* <SitecoreSearchBlockWidget rfkId={searchResultWidgetId}/> */}
                <SitecoreSearchBlock />
            </SearchResultProvider>
        </WidgetsProvider>
    );
};

SitecoreSearchBlockInit.propTypes = {
    el: PropTypes.instanceOf(Element).isRequired,
    props: PropTypes.shape({
        dataSourceId: PropTypes.string,
        env: PropTypes.string,
        customerKey: PropTypes.string,
        apiKey: PropTypes.string,
        searchResultWidgetId: PropTypes.string,
        popularSearchWidgetId: PropTypes.string,
        previewSearchWidgetId: PropTypes.string,
        popularSearchSorting: PropTypes.string,
        popularSearchMaxItems: PropTypes.number,
        searchItemsPerPage: PropTypes.number
    })
};

export default SitecoreSearchBlockInit;
